"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Orbit, Compass, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CosmicOrb } from "@/components/cosmic/CosmicOrb";
export default function UniverseBridge() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="universe-bridge" data-nav="dark" className="relative w-full max-w-full overflow-hidden bg-transparent text-foreground py-14 sm:py-16 md:py-24 border-b border-border">
      <div className="relative mx-auto max-w-[76rem] px-4 sm:px-6 lg:px-8 w-full">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="ql-glass-strong rounded-2xl border border-primary/40 text-card-foreground p-5 sm:p-8 md:p-12 shadow-2xl relative overflow-hidden transition-[border-color,box-shadow] duration-300 hover:border-primary/60 w-full">
          {/* Subtle background ambient orb */}
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-10 right-0 sm:-bottom-12 sm:-right-8 opacity-30 sm:opacity-40">
            <div className="scale-75 sm:scale-100 origin-bottom-right">
              <CosmicOrb size={180} hue="violet" rings={2} />
            </div>
          </div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-8 flex flex-col">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Orbit className="size-4 text-primary shrink-0" />
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  COSMIC INTERCONNECT
                </span>
                <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 font-mono text-[0.68rem] font-medium text-primary">
                  Bridge Available
                </span>
              </div>

              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-snug mb-4 break-words">
                Connect With Create Your Universe
              </h2>

              <p className="text-xs sm:text-sm md:text-base text-muted-foreground leading-relaxed mb-4 break-words">
                You can design a hypothetical cosmos in our parametric synthesizer—fine-tuning dark energy, gravitational curvature, and matter asymmetry—and bring its unique physical laws back here to place all four of your fictional lives inside that custom reality.
              </p>

              <p className="text-xs sm:text-sm md:text-base text-muted-foreground leading-relaxed break-words">
                The connection works both ways: whenever an alternate self inspires your curiosity, open their home cosmos directly inside Create Your Universe with a single click to inspect its stars, nucleosynthesis stages, and long-term evolutionary fate.
              </p>
            </div>

            {/* Right Action Callout */}
            <div className="lg:col-span-4 flex flex-col items-start lg:items-end justify-center w-full">
              <div className="w-full sm:w-auto flex flex-col gap-3">
                <Link to="/CreateYourUniversePage" className="ql-btn w-full sm:w-auto min-h-[44px] inline-flex items-center justify-center gap-2 text-sm sm:text-base text-center">
                  <Compass className="size-4 shrink-0" />
                  <span>Open Create Your Universe</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Link>

                <p className="text-xs text-muted-foreground text-center lg:text-right flex items-center justify-center lg:justify-end gap-1.5">
                  <Sparkles className="size-3 text-primary shrink-0" />
                  <span>Synthesize custom physical laws</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}